import { FERTILIZER_METHODS } from '@cibo/core'
import { filter, sum } from 'ramda'
import * as Yup from 'yup'
import { ITraitFeatureByIdYear, ValidationSchemaFunctionArguments } from '..'
import { eventValidationSchema } from '../EventDetail'
import { isDetailRequired } from '../useIsRequired'
import { findInitialValuesDetail } from '../utils'
import { FertilizerCell } from './FertilizerCell'
import { FertilizerEditor } from './FertilizerEditor'

export const FertilizerEventValidationSchema = ({
  t,
  requirement,
}: ValidationSchemaFunctionArguments) =>
  Yup.object().shape({
    date: Yup.date()
      .required(t('required', { context: 'date' }))
      .typeError(t('required', { context: 'date' })),
    type: Yup.string()
      .oneOf(['starter', 'urea', 'nh3', 'uan28', 'uan30', 'uan32'], t('oneOf', { context: 'type' }))
      [
        isDetailRequired({ requirement, fieldName: 'units' }) ||
        isDetailRequired({ requirement, fieldName: 'type' })
          ? 'required'
          : 'nullable'
      ](t('required', { context: 'type' })),
    units: Yup.string()
      .when('type', {
        is: ['uan28', 'uan30', 'uan32'],
        then: Yup.string().oneOf(['gallonsPerAcre'], t('oneOf', { context: 'units' })),
        otherwise: Yup.string().when('type', {
          is: ['urea', 'nh3'],
          then: Yup.string().oneOf(
            ['poundsPerAcre', 'ouncesPerAcre'],
            t('oneOf', { context: 'units' })
          ),
          otherwise: Yup.string().oneOf(
            ['gallonsPerAcre', 'poundsPerAcre', 'ouncesPerAcre'],
            t('oneOf', { context: 'units' })
          ),
        }),
      })
      [isDetailRequired({ requirement, fieldName: 'units' }) ? 'required' : 'nullable'](
        t('required', { context: 'units' })
      )
      .typeError(t('required', { context: 'units' })),
    rate: Yup.number()
      .min(1, t('min', { context: 'rate' }))
      .when('units', {
        is: 'poundsPerAcre',
        then: Yup.number().max(400, t('max', { context: 'rate', max: 400 })),
        otherwise: Yup.number().when('type', {
          is: 'uan28',
          then: Yup.number().max(137, t('max', { context: 'rate', max: 137 })),
          otherwise: Yup.number().when('type', {
            is: 'uan30',
            then: Yup.number().max(125, t('max', { context: 'rate', max: 125 })),
            otherwise: Yup.number().when('type', {
              is: 'uan32',
              then: Yup.number().max(114, t('max', { context: 'rate', max: 114 })),
              otherwise: Yup.number().max(1000, t('max', { context: 'rate', max: 1000 })),
            }),
          }),
        }),
      })
      .typeError(t('required', { context: 'rate' })),
    method: Yup.string()
      .oneOf(FERTILIZER_METHODS, t('oneOf', { context: 'method' }))
      [isDetailRequired({ requirement, fieldName: 'method' }) ? 'required' : 'nullable'](
        t('required', { context: 'method' })
      ),
    slowRelease: Yup.boolean()
      .typeError(t('required', { context: 'slowRelease' }))
      [isDetailRequired({ requirement, fieldName: 'slowRelease' }) ? 'required' : 'nullable'](
        t('required', { context: 'slowRelease' })
      ),
    inhibitor: Yup.boolean()
      .typeError(t('required', { context: 'inhibitor' }))
      [isDetailRequired({ requirement, fieldName: 'inhibitor' }) ? 'required' : 'nullable'](
        t('required', { context: 'inhibitor' })
      ),
    ammonium: Yup.boolean()
      .typeError(t('required', { context: 'ammonium' }))
      [isDetailRequired({ requirement, fieldName: 'ammonium' }) ? 'required' : 'nullable'](
        t('required', { context: 'ammonium' })
      ),
    nitrogenPer: constituentValidation({ t, requirement, fieldName: 'nitrogenPer' }),
    phosphorusPer: constituentValidation({ t, requirement, fieldName: 'phosphorusPer' }),
    potassiumPer: constituentValidation({ t, requirement, fieldName: 'potassiumPer' }),
    sulfurPer: constituentValidation({ t, requirement, fieldName: 'sulfurPer' }),
    combinedPercent: Yup.mixed().test(
      'componentMax',
      t('max', { context: 'combined' }),
      function () {
        const { sulfurPer, potassiumPer, phosphorusPer, nitrogenPer } = this.parent
        return sum(filter(a => !!a, [sulfurPer, potassiumPer, phosphorusPer, nitrogenPer])) <= 100
      }
    ),
    amountPresent: Yup.mixed().test('amountPresent', t('amountNotPresent'), function () {
      const { type, rate, units, nitrogenAmnt } = this.parent

      const hasTypedAmount =
        typeof type === 'string' && typeof rate === 'number' && typeof units === 'string'
      const hasNitrogenAount = typeof nitrogenAmnt === 'number'

      return (hasTypedAmount && !hasNitrogenAount) || (!hasTypedAmount && hasNitrogenAount)
    }),
    product: Yup.string()[
      isDetailRequired({ requirement, fieldName: 'product' }) ? 'required' : 'nullable'
    ](t('required', { context: 'product' })),
    nitrogenAmnt: Yup.number()
      .typeError(t('required', { context: 'nitrogenAmnt' }))
      [isDetailRequired({ requirement, fieldName: 'nitrogenAmnt' }) ? 'required' : 'nullable'](
        t('required', { context: 'nitrogenAmnt' })
      ),
  })

const TRAIT_ID = 'fertilizer'
const constituentValidation = ({
  fieldName,
  requirement,
  t,
}: ValidationSchemaFunctionArguments & { fieldName: string }) => {
  const stringSchema = Yup.string()
  const numberSchema = Yup.number()
    .min(0, t('min', { contet: 'percent' }))
    .max(100, t('max', { context: 'percent' }))
    .typeError(t('required', { context: 'percent' }))

  return Yup.lazy(value =>
    isDetailRequired({ requirement, fieldName })
      ? value === ''
        ? stringSchema.required(t('required', { context: value }))
        : numberSchema.required(t('required', { context: value }))
      : value === ''
      ? stringSchema.nullable()
      : numberSchema.nullable()
  )
}

export const Fertilizer: ITraitFeatureByIdYear = {
  traitId: TRAIT_ID,
  cellDisplay: FertilizerCell,
  editor: FertilizerEditor,
  initialValues: request => {
    const detail = findInitialValuesDetail(TRAIT_ID)(request)
    return detail?.input || {}
  },
  ns: '@cibo/landmanager/FertilizerEditor',
  validationSchema: eventValidationSchema(props => FertilizerEventValidationSchema(props)),
}
